/* ============================================================
   teg — storage.

   Filesystems, the disks under them, and what each machine says
   about its own SMART health. Directory sizes are a separate,
   explicit action: they walk five large trees with `du` and take
   seconds, which is not something to do on a five-second poll.
   ============================================================ */

import React, { useEffect, useState } from 'react';
import { Icon, Meter, fmtBytes, fmtUptime, level } from './lib';
import { api } from './api';

function Health({ h, label }) {
  if (!h) {
    return (
      <div className="tg-health">
        <Icon name="shield" size={14}/>
        <span>No self-check published by {label}</span>
        <span className="meta">run setup-disk-health.sh there</span>
      </div>
    );
  }
  const stale = h.age_s > 3 * 3600;
  const bad = h.passed === false || (h.pending || 0) > 0 || (h.reallocated || 0) > 0;
  return (
    <div className={`tg-health ${bad ? 'is-bad' : stale ? '' : 'is-ok'}`}>
      <Icon name="shield" size={14}/>
      <span>{h.passed === false ? 'Self-check FAILED' : h.passed ? 'Self-check passed' : 'Self-check inconclusive'}</span>
      {h.temperature_c != null && <span className="meta">{h.temperature_c}°C</span>}
      {h.power_on_hours != null && <span className="meta">{Math.round(h.power_on_hours / 24)} days powered</span>}
      {(h.pending || 0) > 0 && <span className="tg-bad">{h.pending} sectors pending</span>}
      {(h.reallocated || 0) > 0 && <span className="tg-bad">{h.reallocated} reallocated</span>}
      {stale && <span className="meta">checked {fmtUptime(h.age_s)} ago</span>}
      {h.model && <span className="meta">{h.model}</span>}
    </div>
  );
}

export default function Storage({ token, stats }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sizes, setSizes] = useState(null);
  const [sizing, setSizing] = useState(false);

  useEffect(() => {
    let live = true;
    const load = () => api.disks(token)
      .then((d) => { if (live) { setData(d); setErr(''); } })
      .catch((e) => { if (live) setErr(e.message); });
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => { live = false; clearInterval(id); };
  }, [token]);

  const measure = async () => {
    setSizing(true);
    try { setSizes(await api.diskUsage(token)); }
    catch (e) { setErr(e.message); }
    finally { setSizing(false); }
  };

  return (
    <div className="tg-view">
      <div className="tg-view-head">
        <h2 className="h2">Storage</h2>
        {stats?.disk_io && (
          <span className="meta">read {fmtBytes(stats.disk_io.read_per_s)}/s · write {fmtBytes(stats.disk_io.write_per_s)}/s</span>
        )}
      </div>

      {err && <div className="alert alert--err tg-banner">{err}</div>}
      {!data && !err && <div className="skeleton tg-skel"/>}

      {data && (
        <>
          <div className="tg-grid tg-grid--two">
            {data.mounts.map((m) => (
              <div key={m.mount} className="panel tg-mount">
                <div className="panel-head">
                  <span className="tg-mount-name"><Icon name="disk" size={15}/> {m.mount}</span>
                  <span className="meta">{m.fstype}{m.remote ? ' · over the network' : ''}</span>
                </div>
                <div className="tg-mount-body">
                  <div className="tg-mount-figures">
                    <span className="tg-big">{Math.round(m.percent)}%</span>
                    <span className="meta">{fmtBytes(m.used)} of {fmtBytes(m.total)} · {fmtBytes(m.free)} free</span>
                  </div>
                  <Meter pct={m.percent} height={8}
                         hint={`${fmtBytes(m.used)} used · ${fmtBytes(m.free)} free of ${fmtBytes(m.total)}`}/>
                  <div className="meta tg-mount-dev">{m.device}</div>
                  {m.mount.includes('nas-storage') && <Health h={data.health.disinteg} label="disinteg"/>}
                  {m.mount.includes('hp-nas') && <Health h={data.health.hp} label="ojee-hp-zorin"/>}
                </div>
              </div>
            ))}
          </div>

          <div className="panel">
            <div className="panel-head"><span className="label">Devices</span>
              <span className="meta">since boot</span>
            </div>
            <div className="tg-devs">
              {data.devices.map((d) => (
                <div key={d.name} className="tg-dev">
                  <span className="tg-dev-name">/dev/{d.name}</span>
                  <span className="meta">read {fmtBytes(d.read_bytes)}</span>
                  <span className="meta">write {fmtBytes(d.write_bytes)}</span>
                  <span className="meta">{d.read_count + d.write_count} ops</span>
                </div>
              ))}
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <span className="label">Where the space went</span>
              <button type="button" className="btn btn--ghost btn--sm" onClick={measure} disabled={sizing}>
                {sizing ? 'Measuring…' : sizes ? 'Measure again' : 'Measure'}
              </button>
            </div>
            {!sizes && !sizing && (
              <p className="meta tg-note">
                Walks the big directories with <code>du</code>. It takes a few seconds, so it only runs
                when asked.
              </p>
            )}
            {sizes && (
              <div className="tg-sizes">
                {Object.entries(sizes).sort((a, b) => b[1] - a[1]).map(([name, bytes]) => {
                  const max = Math.max(...Object.values(sizes), 1);
                  return (
                    <div key={name} className="tg-size">
                      <span className="tg-size-name">{name.replace(/_/g, ' ')}</span>
                      <span className="bar tg-size-bar tg-hint" tabIndex={0}
                            data-hint={`${name.replace(/_/g, ' ')} · ${fmtBytes(bytes)}`}>
                        <span className="fill" style={{ width: `${(bytes / max) * 100}%` }}/>
                      </span>
                      <span className="tnum">{fmtBytes(bytes)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export { level };
